/**
 * The capability's wire half: a method name and JSON arguments, validated
 * and applied to an `AgentToolsApi`. Every host that serves an eval's tools
 * over a message or a route (Hearth's App bridge, Bear's chat host) parses
 * requests here, so the accepted shapes have one definition.
 */
import { z } from "zod";

import { editToolDescriptor } from "../agents/file-tools";
import type { AgentToolsApi, CoreToolsApi } from "./contract";

const heads = z
  .array(z.string())
  .nullish()
  .transform((value) => value ?? null);

const catalogRequest = z.discriminatedUnion("method", [
  z.object({
    method: z.literal("list"),
    args: z.tuple([z.string().optional()]),
  }),
  z.object({
    method: z.literal("search"),
    args: z.tuple([z.string(), z.string().optional()]),
  }),
  z.object({ method: z.literal("describe"), args: z.tuple([z.string()]) }),
  z.object({
    method: z.literal("call"),
    args: z.tuple([z.string(), z.record(z.string(), z.unknown()).optional()]),
  }),
]);

const coreRequest = z.discriminatedUnion("method", [
  z.object({
    method: z.literal("read"),
    args: z.tuple([
      z.string(),
      z.object({ offset: z.number().optional(), range: z.number().optional() }),
    ]),
  }),
  z.object({
    method: z.literal("write"),
    args: z.tuple([z.string(), z.string(), z.object({ seenHeads: heads })]),
  }),
  z.object({
    method: z.literal("edit"),
    args: z.tuple([editToolDescriptor.inputSchema, heads]),
  }),
  z.object({ method: z.literal("bash"), args: z.tuple([z.string()]) }),
]);

const CORE_METHODS: ReadonlySet<string> = new Set(
  coreRequest.options.map((option) => option.shape.method.value),
);

/** JSON carries an omitted optional argument as null. */
const wireArgs = (args: unknown[]) =>
  args.map((arg) => (arg === null ? undefined : arg));

/** Whether `method` is one of eval's core file globals. */
export function isCoreAgentToolsMethod(method: string): boolean {
  return CORE_METHODS.has(method);
}

/** One of eval's core file globals (`read`, `write`, `edit`, `bash`),
 *  on whichever host holds the files. */
export function invokeCoreAgentTool(
  core: CoreToolsApi,
  method: string,
  args: unknown[],
): Promise<unknown> {
  const input = coreRequest.parse({ method, args: wireArgs(args) });
  switch (input.method) {
    case "read":
      return core.read(...input.args);
    case "write":
      return core.write(...input.args);
    case "edit":
      return core.edit(...input.args);
    case "bash":
      return core.bash(...input.args);
  }
}

/** Any `AgentToolsApi` method, from the wire. */
export function invokeAgentTools(
  api: AgentToolsApi,
  method: string,
  args: unknown[],
): Promise<unknown> {
  if (isCoreAgentToolsMethod(method))
    return invokeCoreAgentTool(api, method, args);
  const input = catalogRequest.parse({ method, args: wireArgs(args) });
  switch (input.method) {
    case "list":
      return api.list(...input.args);
    case "search":
      return api.search(...input.args);
    case "describe":
      return api.describe(...input.args);
    case "call":
      return api.call(...input.args);
  }
}
